import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './InterviewPage.scss';

interface FormData {
  name: string;
  email: string;
  phone: string;
  timeSlot: string;
  connection: string;
  hasMaterials: boolean;
  additionalInfo: string;
  hearAbout: string;
  consent: boolean;
}

interface FormError {
  message: string;
  field?: keyof FormData;
}

export function InterviewPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<FormError | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    timeSlot: '',
    connection: '',
    hasMaterials: false,
    additionalInfo: '',
    hearAbout: '',
    consent: false
  });

  const timeSlots = [
    '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
    '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM'
  ];

  const hearAboutOptions = [
    'Social Media',
    'Word of Mouth',
    'Documentary Crew',
    'Other'
  ];

  const validateForm = (): FormError | null => {
    if (!formData.name.trim()) {
      return { message: 'Name is required', field: 'name' };
    }
    if (!formData.email.trim()) {
      return { message: 'Email is required', field: 'email' };
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return { message: 'Please enter a valid email address', field: 'email' };
    }
    if (!formData.timeSlot) {
      return { message: 'Please select a time slot', field: 'timeSlot' };
    }
    if (!formData.connection.trim()) {
      return { message: 'Please describe your connection to Underground Pizza', field: 'connection' };
    }
    if (!formData.consent) {
      return { message: 'You must consent to being recorded', field: 'consent' };
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('https://dcvzy2zccy2crnkxncsof75t7q0brcek.lambda-url.us-east-1.on.aws/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY || '',
        },
        mode: 'cors',
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        let errorMessage = 'Failed to submit form';
        let errorData: any = null;
        
        try {
          errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          console.error('Error parsing error response:', e);
        }

        // Handle specific error cases
        switch (response.status) {
          case 401:
            setError({
              message: 'Authentication error. Please contact support.'
            });
            break;
          case 409:
            setError({
              message: 'An interview signup with this email already exists',
              field: 'email'
            });
            break;
          case 400:
            setError({
              message: errorData?.error || 'Please check your form inputs',
              field: 'name' // The first required field
            });
            break;
          case 500:
            setError({
              message: 'Server error. Please try again later.'
            });
            break;
          default:
            setError({
              message: errorData?.error || 'Failed to submit form. Please try again.'
            });
        }
        setIsSubmitting(false);
        return;
      }

      // Success case - don't try to parse response if status is 200
      if (response.status === 200) {
        navigate('/thank-you');
      }
    } catch (err) {
      console.error(`Form submission error:`, err);
      setError({ 
        message: 'Network error. Please check your connection and try again.'
      });
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const finalValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
  };

  return (
    <div className="interview-page">
      <div className="form-content">
        <button className="back-button" onClick={() => navigate('/')} aria-label="Back to home">←</button>
        <h1>🍕 Underground Pizza Interview Signup Form</h1>
        <p className="form-intro">We're thrilled you're interested in sharing your Underground Pizza story! Please fill out the form below to sign up for an interview.</p>
        
        <form onSubmit={handleSubmit}>
          <section className="form-section">
            <h2>Contact Information</h2>
            <div className="form-group">
              <label htmlFor="name">Full Name <span className="required">*</span></label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Your full name"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email Address <span className="required">*</span></label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="your@email.com"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Phone Number <span className="optional">(optional)</span></label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(601) 555-0123"
              />
            </div>
          </section>

          <section className="form-section">
            <h2>Availability</h2>
            <p className="interview-date">Interviews will be conducted on <strong>January 25th</strong> in Hattiesburg, MS.</p>
            <div className="form-group">
              <label htmlFor="timeSlot">Preferred Time Slot <span className="required">*</span></label>
              <select
                id="timeSlot"
                name="timeSlot"
                value={formData.timeSlot}
                onChange={handleChange}
                required
              >
                <option value="">Select a time slot</option>
                {timeSlots.map(slot => (
                  <option key={slot} value={slot}>{slot}</option>
                ))}
              </select>
              <p className="field-note">Please select one 30-minute time slot between 9AM and 6PM.</p>
            </div>
          </section>

          <section className="form-section">
            <h2>Your Story</h2>
            <div className="form-group">
              <label htmlFor="connection">What is your connection to Underground Pizza? <span className="required">*</span></label>
              <textarea
                id="connection"
                name="connection"
                value={formData.connection}
                onChange={handleChange}
                required
                placeholder="Please tell us briefly about your experiences, memories, or the role Underground Pizza played in your life."
                rows={4}
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="hasMaterials"
                  checked={formData.hasMaterials}
                  onChange={handleChange}
                />
                Yes, I have materials to share (photos, videos, or other materials)
              </label>
            </div>
          </section>

          <section className="form-section">
            <h2>Additional Information</h2>
            <div className="form-group">
              <label htmlFor="additionalInfo">Anything else you'd like us to know? <span className="optional">(optional)</span></label>
              <textarea
                id="additionalInfo"
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleChange}
                placeholder="Share any additional information you think might be relevant"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="hearAbout">How did you hear about this project? <span className="optional">(optional)</span></label>
              <select
                id="hearAbout"
                name="hearAbout"
                value={formData.hearAbout}
                onChange={handleChange}
              >
                <option value="">Select an option</option>
                {hearAboutOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </section>

          <section className="form-section">
            <h2>Consent</h2>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="consent"
                  checked={formData.consent}
                  onChange={handleChange}
                  required
                />
                I consent to be recorded for the documentary <span className="required">*</span>
              </label>
              <p className="field-note">By submitting this form, you agree to be contacted by the Underground Pizza team and consent to being recorded for the documentary.</p>
            </div>
          </section>
          
          {error && (
            <div className="error-message">
              {error.message}
            </div>
          )}
          
          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
